// ---------------------------------------------------------------------------
// Page status transitions (state machine)
// ---------------------------------------------------------------------------

import type { PageStatus } from "./schema/index.js";
export { COMPONENT_TYPES, type ComponentType } from "./block-registry.js";

export const STATUS_TRANSITIONS: Record<PageStatus, PageStatus[]> = {
  draft: ["published", "archived"],
  published: ["draft", "archived"],
  archived: ["draft"],
} as const;

// ---------------------------------------------------------------------------
// Brand voice options
// ---------------------------------------------------------------------------

export const BRAND_VOICES = [
  "professional",
  "casual",
  "playful",
  "authoritative",
  "friendly",
] as const;

export type BrandVoice = (typeof BRAND_VOICES)[number];

// ---------------------------------------------------------------------------
// Performance budgets (bytes)
// ---------------------------------------------------------------------------

export const PERFORMANCE_BUDGET = {
  HTML_MAX_BYTES: 50 * 1024,
  CSS_CRITICAL_MAX_BYTES: 15 * 1024,
  CSS_DEFERRED_MAX_BYTES: 30 * 1024,
  JS_FIRST_PARTY_MAX_BYTES: 30 * 1024,
  JS_THIRD_PARTY_MAX_BYTES: 50 * 1024,
  IMAGE_ABOVE_FOLD_MAX_BYTES: 200 * 1024,
  TOTAL_PAGE_MAX_BYTES: 500 * 1024,
} as const;

// ---------------------------------------------------------------------------
// Rate limits
// ---------------------------------------------------------------------------

export const RATE_LIMITS = {
  FORM_SUBMISSIONS_PER_IP_PER_MIN: 10,
  AI_GENERATIONS_PER_TENANT_PER_HOUR: 50,
} as const;

// ---------------------------------------------------------------------------
// Version limits
// ---------------------------------------------------------------------------

export const VERSION_LIMITS = {
  MAX_HOT_VERSIONS: 50,
} as const;

// ---------------------------------------------------------------------------
// Image srcset widths for responsive images
// ---------------------------------------------------------------------------

export const IMAGE_SRCSET_WIDTHS = [320, 640, 1024, 1440] as const;

// ---------------------------------------------------------------------------
// OG image dimensions
// ---------------------------------------------------------------------------

export const OG_IMAGE_WIDTH = 1200;
export const OG_IMAGE_HEIGHT = 630;

// ---------------------------------------------------------------------------
// Blog engine
// ---------------------------------------------------------------------------

export const BLOG_MAX_CATEGORIES = 3;
export const BLOG_MAX_TAGS = 10;
export const BLOG_POSTS_PER_PAGE = 10;
export const BLOG_RSS_ITEM_COUNT = 20;
export const BLOG_READING_SPEED_WPM = 200;
export const BLOG_MAX_SLUG_LENGTH = 80;
export const BLOG_COMMENT_MAX_LENGTH = 5000;
export const BLOG_COMMENTS_PER_PAGE = 20;
export const BLOG_COMMENT_AUTHOR_MAX_LENGTH = 200;

// ---------------------------------------------------------------------------
// Funnel engine
// ---------------------------------------------------------------------------

export const FUNNEL_MAX_STEPS = 10;
export const FUNNEL_SESSION_TTL_HOURS = 24;
export const FUNNEL_SESSION_STORAGE_KEY_PREFIX = "ease_fs_";

// ---------------------------------------------------------------------------
// A/B testing
// ---------------------------------------------------------------------------

export const AB_MAX_VARIANTS = 8;
export const AB_MIN_SPLIT_PCT = 1;
export const AB_MIN_SAMPLE_SIZE = 200;
export const AB_SIGNIFICANCE_ALPHA = 0.05;
export const AB_COOKIE_PREFIX = "ease_ab_";
export const AB_COOKIE_MAX_AGE_SEC = 2_592_000; // 30 days
export const AB_METADATA_KEY = "ab_experiment";

// ---------------------------------------------------------------------------
// Lighthouse audit
// ---------------------------------------------------------------------------

export const AUDIT_THRESHOLDS = {
  starter: null,
  professional: { poor: 70, warning: 80 },
  enterprise: { poor: 80, warning: 90 },
} as const;

export const AUDIT_COOLDOWN_MS = {
  professional: 24 * 60 * 60 * 1000,
  enterprise: 1 * 60 * 60 * 1000,
} as const;

export const AUDIT_HISTORY_LIMIT = 30;
export const PSI_API_BASE_URL = "https://www.googleapis.com/pagespeedonline/v5/runPagespeed";
export const PSI_STRATEGY = "mobile" as const;
export const PSI_FIELDS = [
  "lighthouseResult.categories",
  "lighthouseResult.audits.largest-contentful-paint",
  "lighthouseResult.audits.total-blocking-time",
  "lighthouseResult.audits.cumulative-layout-shift",
  "lighthouseResult.audits.first-contentful-paint",
  "lighthouseResult.audits.server-response-time",
].join(",");

// ---------------------------------------------------------------------------
// AI Generation Checkpoints
// ---------------------------------------------------------------------------

export const CHECKPOINT_MAX_VERSIONS = 10;
export const CHECKPOINT_EXPIRY_HOURS = 24;
export const CHECKPOINT_MAX_CONCURRENT = 3;

export const GENERATION_LIMITS_PER_HOUR = {
  starter: 10,
  professional: 50,
  enterprise: 200,
} as const;
